import { Component } from 'react';
import {
  Alert,
  Button,
  ControlLabel,
  DropdownButton,
  FormControl,
  FormGroup,
  MenuItem,
  Modal,
} from 'react-bootstrap';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import _ from 'underscore';
import type { MouseEvent } from 'react';

import { PolicyType } from '../../../typings/enums';
import { isQualityEnabled } from '../../helpers/features/quality';
import { isSecurityEnabled } from '../../helpers/features/security';
import { userHasPermissionToCreatePolicyType } from '../../helpers/permissions';
import PolicyModule from '../../redux/policies';
import { DocsLink } from '../generic/Link';
import type { Policy } from '../../types';

const _templates = {
  standard_bundle: {
    title: 'Standard Bundle Distribution',
    description:
      'Policy template for bundles where distribution type is flexible (i.e. most enterprise software, multi binary/process bundles).',
  },
  single_binary: {
    title: 'Single-Binary Distribution',
    description:
      'Policy template for when all code and resources are linked & bundled into a single binary distribution (i.e. most mobile apps, embedded systems, or compiled binary releases).',
  },
  hosted_service: {
    title: 'Website/Hosted Service',
    description:
      'Policy template for when all application code & libraries stand behind a server (i.e. most SaaS, websites, API services).',
  },
};

type IModalCreatePolicyProps = {
  onHide(event?: MouseEvent<{}>): void;
  show: boolean;
  policyType: PolicyType;
  policyList: Policy[];
};

type IModalCreatePolicyState = {
  title?: string;
  template?: string;
  description?: string;
  type: PolicyType;
  error?: string;
  extendedPolicyId?: number;
};

class ModalCreatePolicy extends Component<IModalCreatePolicyProps, IModalCreatePolicyState> {
  constructor(props) {
    super(props);
    this.state = {
      title: '',
      template: null,
      description: null,
      extendedPolicyId: null,
      type: this.props.policyType || PolicyType.LICENSING,
    };
  }

  onSubmit = async () => {
    const data: { [key: string]: string } = {};
    _.mapObject(_.pick(this.state, 'type', 'title', 'description', 'template', 'extendedPolicyId'), (val, key) => {
      // filter null
      if (val) {
        data[key] = val;
      }
    });

    if (!data.title) {
      this.setState({ error: 'Please provide a title for this policy' });
      return;
    }

    await this.props.createPolicy(data);
    this.setState({ title: '', description: '', error: null });
    this.props.onHide();
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.policyType !== nextProps.policyType) {
      this.setState({ type: nextProps.policyType || PolicyType.LICENSING });
    }
  }

  onTemplateSelect = template => {
    this.setState({ template, extendedPolicyId: null });
  };

  onExistingPolicySelect = policyId => {
    this.setState({ extendedPolicyId: policyId, template: null });
  };

  canCreateSBOMPolicy = () => userHasPermissionToCreatePolicyType(PolicyType.SBOM);

  renderTemplates = () => {
    const templateLabel = {
      textTransform: 'uppercase',
      color: '#35465e',
      fontWeight: 'bold',
      textAlign: 'center',
    };
    const flexStyle = {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'nowrap',
    };
    const extendablePolicies = _
      .chain(this.props.policyList || [])
      .filter(existingPolicy => existingPolicy.type === PolicyType.LICENSING)
      .map(existingPolicy => {
        if (_templates[existingPolicy.title]) {
          existingPolicy.title = _templates[existingPolicy.title].title;
        }
        return existingPolicy;
      })
      .value();

    let extendedPolicyTitle;
    if (this.state.extendedPolicyId) {
      // @ts-ignore: ignore type check here
      extendedPolicyTitle = (_.find(extendablePolicies, existingPolicy => existingPolicy.id === this.state.extendedPolicyId) || {}).title;
    }

    return (<div className="form-group">
      <label className="control-label col-xs-3">Template</label>
      <div className="col-xs-9">
        <div style={flexStyle}>
          <div>
            {extendablePolicies && !_.isEmpty(extendablePolicies) ? <label style={templateLabel}>Fossa Template</label> : null }
            <DropdownButton
              onSelect={this.onTemplateSelect}
              className="dropdown-mid"
              title={_templates[this.state.template] ? _templates[this.state.template].title : 'No Template'}>
              <MenuItem eventKey={null}>No Template</MenuItem>
              <MenuItem divider />
              {_.map(Object.keys(_templates), (name) => (
                <MenuItem className="dropdown-mid-item" eventKey={name} key={name}>
                  <b>{_templates[name].title}</b>
                  <p>{_templates[name].description}</p>
                </MenuItem>
              ))}
            </DropdownButton>
          </div>
          {extendablePolicies && !_.isEmpty(extendablePolicies) && (
            <div style={flexStyle}>
              <div style={{
                alignSelf: 'center',
                margin: '0 25px',
              }}>OR
              </div>
              <div>
                <label style={templateLabel}>Existing Policy</label>
                <DropdownButton
                  id="modalExtendPolicyDropdownButton"
                  onSelect={this.onExistingPolicySelect}
                  className="dropdown-mid"
                  title={extendedPolicyTitle || 'No Policy'}>
                  <MenuItem eventKey={null}>No Policy</MenuItem>
                  <MenuItem divider />
                  {_.map(extendablePolicies, (existingPolicy, _) => (
                    <MenuItem className="dropdown-mid-item" eventKey={existingPolicy.id} key={existingPolicy.id}>
                      <b>{existingPolicy.title}</b>
                      <p>{existingPolicy.description}</p>
                    </MenuItem>
                  ))}
                </DropdownButton>
              </div>
            </div>
          )}
        </div>
        <p style={{ marginTop: 20 }}>
          Our Policies have been vetted by open source lawyers &amp; experts.
          {' '}
          <DocsLink to="policies">
            Learn More.
          </DocsLink>
        </p>
      </div>
    </div>);
  };

  render() {
    return (
      <Modal {...this.props}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Policy</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {this.state.error
            ? <Alert bsStyle="error">{this.state.error}</Alert>
            : null}
          <form className="form-horizontal">
            {isSecurityEnabled() || isQualityEnabled() || this.canCreateSBOMPolicy() ?
              <FormGroup controlId="policyTypeForm">
                <div className="col-xs-3">
                  <ControlLabel className="col-xs-3">Type</ControlLabel>
                </div>
                <div className="col-xs-9">
                  <DropdownButton
                    id="modalCreatePolicyDropdownButton"
                    onSelect={(type: PolicyType) => {
                      this.setState({ type });
                    }}
                    className="dropdown-mid"
                    title={this.state.type}>
                    <MenuItem eventKey={PolicyType.LICENSING}>Licensing</MenuItem>
                    {isSecurityEnabled() ? <MenuItem eventKey={PolicyType.SECURITY}>Security</MenuItem> : null}
                    {isQualityEnabled() ? <MenuItem eventKey={PolicyType.QUALITY}>Quality</MenuItem> : null}
                    {this.canCreateSBOMPolicy() ? <MenuItem eventKey={PolicyType.SBOM}>SBOM</MenuItem> : null}
                  </DropdownButton>
                </div>
              </FormGroup> : null}
            <FormGroup controlId="policyTitleForm">
              <div className="col-xs-3">
                <ControlLabel className="col-xs-3">Title</ControlLabel>
              </div>
              <div className="col-xs-9">
                <FormControl
                  className="col-xs-9"
                  type="text"
                  placeholder="Mobile App Policy"
                  value={this.state.title}
                  onChange={(ev: any) => {
                    this.setState({ title: ev.target.value });
                  }}
                />
              </div>
            </FormGroup>
            <FormGroup controlId="policyDescriptionForm">
              <div className="col-xs-3">
                <ControlLabel>Description</ControlLabel>
              </div>
              <div className="col-xs-9">
                <FormControl
                  componentClass="textarea"
                  placeholder="Standard policy for all mobile apps distributed by Company X."
                  value={this.state.description}
                  onChange={(ev: any) => {
                    this.setState({ description: ev.target.value });
                  }}
                />
              </div>
            </FormGroup>
            {this.state.type === PolicyType.LICENSING ? this.renderTemplates() : null }
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={this.props.onHide}>Cancel</Button>
          <Button onClick={this.onSubmit} bsStyle="primary">
            Submit
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  policyList: PolicyModule.selectors.getPolicyList,
});

export default connect(mapStateToProps, {
  createPolicy: PolicyModule.actions.create,
})(ModalCreatePolicy);
